import React, { useCallback, useEffect, useMemo, useState } from 'react';
import _ from 'underscore';
import api from 'helpers/api';
import dateFormatter from 'helpers/date_formatter';
import notification from 'helpers/notification';
import range from 'helpers/range';
import useGym from 'helpers/use_gym';
import RadioButtons from 'react/components/radio_buttons';
import Select from 'react/components/select';
import moment from 'moment';
import useCurrentUser from 'helpers/use_current_user';
import { useAngularService } from 'helpers/get_angular_service';

interface ContactBulkBookingSelectorProps {
  booking: any;
  contactForBooking: any;
  event: any;
  onChange: (booking: any) => void;
  recurringBookingError?: string;
  showMakeupClassOption?: boolean;
  failedBookings?: any;
}

const ContactBulkBookingSelector = ({
  booking,
  event,
  contactForBooking,
  onChange,
  recurringBookingError,
  showMakeupClassOption,
  failedBookings,
}: ContactBulkBookingSelectorProps) => {
  const eventId = booking.event.id;

  const EventService = useAngularService('EventService');
  const gym = useGym();
  const currentUser = useCurrentUser();

  const [bookAllLimits, setBookAllLimits] = useState(null);
  const [alreadyEnabledForContact, setAlreadyEnabledForContact] = useState(null);

  const recurringBookingsEnabled = useMemo(
    () =>
      gym.gym_config.recurring_bookings_feature_enabled &&
      event?.event_schedule_id &&
      event?.future_events_count > 1,
    [
      event?.event_schedule_id,
      event?.future_events_count,
      gym.gym_config.recurring_bookings_feature_enabled,
    ],
  );

  const getBookAllLimits = useCallback(() => {
    if (!eventId) return;
    const url = `gyms/${gym.id}/calendar/events/${eventId}/book_all_limits`;
    api.get(url).then(
      (data) => {
        setBookAllLimits(data);
      },
      () => {
        notification.error('Request could not be completed. Please try again');
      },
    );
  }, [eventId, gym.id]);

  const checkContactRecurringBookings = useCallback(() => {
    if (!event || !eventId || !recurringBookingsEnabled) return;
    const params = {
      event_schedule_id: event.event_schedule_id,
      contact_id: contactForBooking.id,
    };

    EventService.getRecurringBookings(params).then(
      (recurringBookings) => {
        setAlreadyEnabledForContact(!!recurringBookings.length);
      },
      () => {
        notification.error('Request could not be completed. Please try again');
      },
    );
  }, [EventService, contactForBooking.id, event, eventId, recurringBookingsEnabled]);

  useEffect(() => {
    getBookAllLimits();
  }, [getBookAllLimits]);

  useEffect(() => {
    checkContactRecurringBookings();
  }, [checkContactRecurringBookings]);

  const gymNow = moment().tz(gym.tz).format('MMM D, YYYY h:mma');
  const isLastEvent = useMemo(() => event?.future_events_count === 1, [event?.future_events_count]);

  const eventFullButHasAbsentBookingsMessageVisible = useMemo(() => {
    if (!event) return false;
    return !event.vacancies_count && event.non_absent_bookings_count < event.participant_limit;
  }, [event]);

  const hasMembershipForRecurringBooking = useMemo(() => {
    if (!event) return false;
    return (
      event.free_class ||
      booking.free_class ||
      contactForBooking.recurring_booking_eligible_memberships_exist
    );
  }, [booking.free_class, contactForBooking.recurring_booking_eligible_memberships_exist, event]);

  const disabledOptions = useMemo(() => {
    let options = [];

    if (isLastEvent || eventFullButHasAbsentBookingsMessageVisible) {
      options = ['some', 'all'];
    }

    if (alreadyEnabledForContact || !hasMembershipForRecurringBooking) {
      options.push('recurring');
    }

    return options;
  }, [
    alreadyEnabledForContact,
    eventFullButHasAbsentBookingsMessageVisible,
    hasMembershipForRecurringBooking,
    isLastEvent,
  ]);

  const numberOfEvents = useMemo(() => {
    if (!event) return [];
    return range(1, event.future_events_count);
  }, [event]);

  const eventIsInThePast = useMemo(() => {
    if (!event) return;
    return moment(gymNow, 'MMM D, YYYY h:mma').isAfter(moment(event.start_at, 'YYYY/MM/DD HH:mm'));
  }, [event, gymNow]);

  const bookFromPolicyOptions = useMemo(() => {
    return _.compact([
      !eventIsInThePast && { value: 'book_from_current_event', label: 'This event' },
      { value: 'book_from_now', label: 'Now' },
    ]);
  }, [eventIsInThePast]);

  if (!event || !bookAllLimits) return null;

  const renderRadioLabel = (type: string) => {
    if (type === 'one') {
      return 'Book only this event';
    }

    if (type === 'some') {
      return (
        <div className="inline-compact">
          <div className="inline-module">Next</div>
          <div className="inline-module">
            <Select
              options={numberOfEvents}
              value={booking.count || 1}
              disabled={!booking.count}
              onChange={(count) => onChange({ count })}
              size="xs"
            />
          </div>
          <div className="inline-module">Events in series</div>
        </div>
      );
    }

    if (type === 'all') {
      return (
        <div>
          {((!bookAllLimits.date_limit && !bookAllLimits.class_pack_limit) ||
            booking.free_class) && (
            <div>
              Book all future events in series (
              {booking.free_class ? event.future_events_count : bookAllLimits.event_count}{' '}
              remaining)
            </div>
          )}
          {bookAllLimits.date_limit && !booking.free_class && !bookAllLimits.class_pack_limit && (
            <div>
              Book all future events until membership expires
              <div className="helper-text helper-text--no-margin">
                Member's last membership expires{' '}
                <strong>{dateFormatter(bookAllLimits.date_limit)}</strong>
              </div>
              <div className="helper-text helper-text--no-margin">
                <strong>{bookAllLimits.event_count}</strong> of{' '}
                <strong>{event.future_events_count}</strong> remaining events will be booked.
              </div>
            </div>
          )}
          {bookAllLimits.class_pack_limit && !booking.free_class && !bookAllLimits.date_limit && (
            <div>
              Book all future events until membership expires
              <div className="helper-text helper-text--no-margin">
                <strong>{bookAllLimits.class_pack_limit}</strong> bookings remaining on member's
                current memberships(s)
                <br />
                <strong>{bookAllLimits.class_pack_limit}</strong> of{' '}
                <strong>{event.future_events_count}</strong> remaining events will be booked.
              </div>
            </div>
          )}
          {bookAllLimits.class_pack_limit && !booking.free_class && bookAllLimits.date_limit && (
            <div>
              Book all future events until membership expires
              <div className="helper-text helper-text--no-margin">
                <strong>{bookAllLimits.class_pack_limit}</strong> bookings remaining on member's
                current memberships(s)
                <br />
                (Last membership expires on {bookAllLimits.date_limit})<br />
                <strong>{bookAllLimits.class_pack_limit}</strong> of{' '}
                <strong>{event.future_events_count}</strong> remaining events will be booked (or up
                to membership expiry).
              </div>
            </div>
          )}
        </div>
      );
    }

    if (type === 'recurring') {
      return (
        <div>
          <div>
            Enable recurring bookings
            {alreadyEnabledForContact && (
              <div className="helper-text helper-text--no-margin">
                Recurring bookings are already enabled for this series
              </div>
            )}
            {!alreadyEnabledForContact && hasMembershipForRecurringBooking && (
              <div className="helper-text helper-text--no-margin">
                Book all events that exist now, and any new events that are added in future
              </div>
            )}
            {!alreadyEnabledForContact && !hasMembershipForRecurringBooking && (
              <div className="helper-text helper-text--no-margin">
                Member has no memberships that can be used with recurring bookings
              </div>
            )}
          </div>

          {booking.type === 'recurring' && (
            <div>
              <div className="inline-compact margin-left form-group">
                <div className="inline-module">Book all events from</div>

                <div className="inline-module">
                  <Select
                    options={bookFromPolicyOptions}
                    value={booking.book_from_policy}
                    onChange={(book_from_policy) => onChange({ book_from_policy })}
                    size="xs"
                  />
                </div>
                <div className="inline-module">onwards</div>

                <div className="italic">
                  This will attempt to book all events in this series from{' '}
                  {booking.book_from_policy === 'book_from_current_event' && (
                    <span>{dateFormatter(event.start_at, 'mediumDate')} </span>
                  )}{' '}
                  {booking.book_from_policy === 'book_from_now' && <span>{gymNow} </span>} onwards
                </div>
                {recurringBookingError && (
                  <div className="alert alert-danger is-sm">
                    Cannot book the currently selected event: {recurringBookingError}
                  </div>
                )}
              </div>
              <strong>This will attempt to book all available events in the series.</strong>
              <br />
              <strong>
                This may take a few minutes: an email summary will be sent to {currentUser.email}{' '}
                after this process is complete.
              </strong>
            </div>
          )}
        </div>
      );
    }
  };

  return (
    <div>
      <div className="form-group">
        <label className="control-label">How many bookings?</label>
        <RadioButtons
          vertical
          options={_.compact(['one', 'some', 'all', recurringBookingsEnabled && 'recurring'])}
          value={booking.type}
          onChange={(type) =>
            onChange({
              type,
              count: type === 'some' ? 1 : undefined,
              book_from_policy: type === 'recurring' ? 'book_from_now' : undefined,
            })
          }
          getValue={(type) => type}
          getLabel={renderRadioLabel}
          disabledOptions={disabledOptions}
          alignTop
        />

        {failedBookings && (
          <div>
            {failedBookings.message && (
              <div>
                <span className="fail-message">{failedBookings.message} </span>
                {showMakeupClassOption && (
                  <span className="fail-message">
                    Or, consider using the make-up class option below.
                  </span>
                )}
              </div>
            )}

            <div className="margin-top-8">
              {failedBookings.bookings.invalid.map((item, index) => (
                <div key={index} className="fail-message">
                  {dateFormatter(item.start_at, 'mediumDate')} - {item.validation_error}
                </div>
              ))}
            </div>
          </div>
        )}
        {isLastEvent && (
          <div className="text-grey">
            <br />
            This is the final event in this series, please add a new series to book future events.
          </div>
        )}
      </div>
    </div>
  );
};

angular.module('gym').value('ContactBulkBookingSelector', ContactBulkBookingSelector);
